import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { userAnswersAPI, usersAPI, notificationsAPI } from "../api";
import { getUnreadCount } from "../utils/notificationsRead";
import {
  BookOpen,
  Lightbulb,
  GraduationCap,
  Sparkles,
  MessageSquare,
  Users,
  Globe,
  User,
  ChevronRight,
  Shield,
  Settings,
  LogOut,
  Bell,
  Maximize,
  Minimize,
  BarChart2,
  Video,
  Swords,
  Trophy,
  Flag,
  Layers,
  AlertTriangle,
  ListOrdered,
  Puzzle,
  Bookmark,
  Ticket,
  MapPin,
  Zap,
  Timer,
  Headphones,
  X as XIcon,
  Play,
} from "lucide-react";
import { useLang } from "../context/LangContext";
import { BRAND, BRAND_BG, BRAND_BORDER } from "../theme/colors";
import toast from "react-hot-toast";

interface Stats {
  mistakes: number;
  ticketsDone: number;
  topicsDone: number;
  streak: number;
}

const comingSoon = () => toast("🚀 Tez orada qo'shiladi");
const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const [stats, setStats] = useState<Stats>({
    mistakes: 0,
    ticketsDone: 0,
    topicsDone: 0,
    streak: 0,
  });
  const [readiness, setReadiness] = useState(0);
  const [showLang, setShowLang] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [isFullscreen, setIsFullscreen] = useState(
    isIOS ?
      (window.navigator as any).standalone === true
    : !!document.fullscreenElement,
  );

  const toggleFullscreen = async () => {
    if (isIOS) {
      if (!(window.navigator as any).standalone) {
        toast("📱 Safari → Ulashish (☐↑) → Bosh ekranga qo'shish", {
          duration: 4000,
        });
      }
      return;
    }
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.error("Fullscreen xato:", e);
    }
  };

  useEffect(() => {
    if (isIOS) return;
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  useEffect(() => {
    notificationsAPI
      .getAll()
      .then((res) => {
        const ids = (res.data || []).map((n: { id: number }) => n.id);
        setUnreadCount(getUnreadCount(ids));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([usersAPI.getProgress(), userAnswersAPI.getCount()])
      .then(([progressRes, mistakesRes]) => {
        const d = progressRes.data?.data || progressRes.data || {};
        const ticketProgress = d.ticketProgress || d.tickets?.progress || [];
        const topicProgress = d.topicProgress || d.topics?.progress || [];

        const ticketsDone = (
          Array.isArray(ticketProgress) ? ticketProgress : []).filter(
          (x: any) => Number(x?.bestScore || 0) >= 85,
        ).length;

        const topicsDone = (
          Array.isArray(topicProgress) ? topicProgress : []).filter(
          (x: any) => Number(x?.bestScore || 0) >= 85,
        ).length;

        const mistakesCount = Number(mistakesRes.data?.count || 0);

        const ticketPct = (ticketsDone / 61) * 40;
        const topicPct = (topicsDone / 42) * 40;
        const somethingDone = ticketsDone > 0 || topicsDone > 0;
        const mistakePct =
          somethingDone ?
            mistakesCount === 0 ?
              20
            : Math.max(0, 20 - Math.floor(mistakesCount / 5))
          : 0;

        setReadiness(
          Math.min(100, Math.round(ticketPct + topicPct + mistakePct)),
        );

        setStats({
          mistakes: mistakesCount,
          ticketsDone: d.ticketsDone || d.tickets?.done || ticketsDone || 0,
          topicsDone: d.topicsDone || d.topics?.done || topicsDone || 0,
          streak:
            d.streak || d.currentStreak || d.summary?.completedSessions || 0,
        });
      })
      .catch((e) => console.error("Dashboard stats xato:", e));
  }, []);

  const statItems = useMemo(
    () => [
      {
        label: t("wrongQuestions"),
        value: stats.mistakes,
        icon: XIcon,
        color: "#ef4444",
      },
      {
        label: t("ticketsSolved"),
        value: stats.ticketsDone,
        icon: Ticket,
        color: "#f59e0b",
      },
      {
        label: t("topicsSolved"),
        value: stats.topicsDone,
        icon: BookOpen,
        color: "#14b8a6",
      },
    ],
    [stats, t],
  );

  const heroCategories = [
    {
      icon: Trophy,
      title: t("realExam"),
      subtitle: t("realExamSub"),
      badge: "⏱ 25 daqiqa · 20 savol",
      path: "/quiz/exam",
      gradient: "linear-gradient(135deg, #60a5fa, #3b82f6)",
    },
    {
      icon: Swords,
      title: "Oktagon",
      subtitle: t("duelSub"),
      badge: "⚔ PVP rejim",
      path: "/duel",
      gradient: "linear-gradient(135deg, #1e1b3a, #2d1f4d)",
    },
  ];

  const testCategories = [
    {
      icon: Flag,
      title: t("controlExam"),
      shortTitle: t("controlExamShort"),
      subtitle: t("nazoratSub"),
      path: "/quiz/nazorat",
      color: "#14b8a6",
    },
    {
      icon: Layers,
      title: t("testsByTopics"),
      shortTitle: t("testsByTopicsShort"),
      subtitle: t("topicsSub"),
      path: "/topics",
      color: "#64748b",
    },
    {
      icon: Settings,
      title: t("customTests"),
      shortTitle: t("customTestsShort"),
      subtitle: t("customSub"),
      path: "/custom",
      color: "#22c55e",
    },
    {
      icon: Ticket,
      title: t("tickets"),
      shortTitle: t("tickets"),
      subtitle: t("ticketsSub"),
      path: "/tickets",
      color: "#f59e0b",
    },
    {
      icon: AlertTriangle,
      title: t("myWrongQuestions"),
      shortTitle: t("myWrongQuestionsShort"),
      subtitle: t("mistakesSub"),
      path: "/quiz/mistakes",
      color: "#ef4444",
      badge: stats.mistakes > 0 ? String(stats.mistakes) : undefined,
    },
    {
      icon: ListOrdered,
      title: t("numberTests"),
      shortTitle: t("numberTestsShort"),
      subtitle: t("numberTestsSub"),
      path: "/quiz/sonli",
      color: "#64748b",
    },
    {
      icon: Puzzle,
      title: t("complexTests"),
      shortTitle: t("complexTestsShort"),
      subtitle: t("complexTestsSub"),
      path: "/quiz/murakkab",
      color: "#22c55e",
    },
    {
      icon: Bookmark,
      title: t("savedTests"),
      shortTitle: t("savedTestsShort"),
      subtitle: t("savedTestsSub"),
      path: "/favorites",
      color: "#ef4444",
    },
  ];

  const gameCategories = [
    {
      icon: Zap,
      title: t("marathon"),
      subtitle: t("marathonSub"),
      path: "/quiz/marafon",
      color: "#f59e0b",
    },
    {
      icon: Timer,
      title: "Time Attack",
      subtitle: t("timeAttackSub"),
      path: "/time-attack",
      color: "#64748b",
    },
    {
      icon: Swords,
      title: "Duel",
      subtitle: t("duelSub"),
      path: "/duel",
      color: "#ef4444",
      badge: t("badgeNew"),
    },
  ];

  const learnCategories = [
    {
      icon: Lightbulb,
      title: t("catKnowledge"),
      shortTitle: t("catKnowledgeShort"),
      subtitle: t("catKnowledgeSub"),
      path: "/knowledge",
      color: "#f59e0b",
    },
    {
      icon: GraduationCap,
      title: t("catShpargalka"),
      shortTitle: t("catShpargalka"),
      subtitle: t("catShpargalkaSub"),
      path: "/shpargalka",
      color: "#22c55e",
    },
    {
      icon: Video,
      title: t("catVideos"),
      shortTitle: t("catVideosShort"),
      subtitle: t("catVideosSub"),
      path: "/videos",
      color: "#64748b",
    },
    {
      icon: MapPin,
      title: t("catSchools"),
      shortTitle: t("catSchools"),
      subtitle: t("catSchoolsSub"),
      path: "yandex-link",
      color: "#ef4444",
    },
  ];

  const communityCategories = [
    ...(user?.role === "teacher" ?
      [
        {
          icon: Users,
          title: t("myGroups"),
          subtitle: t("myGroupsSub"),
          path: "/teacher-panel",
          color: "#8b5cf6",
        },
      ]
    : []),
  ];

  const navLinks = [
    { label: t("navHome"), path: "/dashboard" },
    { label: t("tickets"), path: "/tickets" },
    { label: t("navTopicsShort"), path: "/topics" },
    { label: "Oktagon", path: "/games" },
  ];

  const langs = [
    { code: "oz", label: "O'zbekcha" },
    { code: "uz", label: "Ўзбекча" },
    { code: "ru", label: "Русский" },
  ];

  const cardBase: React.CSSProperties = {
    background: "var(--card)",
    border: "1px solid var(--border)",
    borderRadius: 14,
  };

  const isAdmin = user?.role === "admin";

  const handleCategoryClick = (path: string) => {
    if (path === "yandex-link") {
      window.open(
        "https://yandex.uz/maps/10335/tashkent/category/driving_school/184105264/",
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (path === "coming-soon") {
      comingSoon();
      return;
    }
    navigate(path);
  };

  const SectionDivider: React.FC<{ label: string; color?: string }> = ({
    label,
    color = BRAND,
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
      }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span
        style={{
          color,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: 1.2,
          textTransform: "uppercase",
        }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );

  const CategoryGrid: React.FC<{
    items: {
      icon: any;
      title: string;
      shortTitle?: string;
      subtitle: string;
      path: string;
      color: string;
      badge?: string;
    }[];
    minWidth?: number;
    columns?: number;
    delayClass?: string;
    large?: boolean;
  }> = ({
    items,
    minWidth = 200,
    columns,
    delayClass = "s2",
    large = false,
  }) => (
    <div
      className={`fade-up ${delayClass}`}
      style={{
        display: "grid",
        gridTemplateColumns:
          columns ?
            `repeat(${columns}, minmax(0, 1fr))`
          : `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
        gap: large ? 10 : 8,
        flexShrink: 0,
      }}>
      {items.map((cat) => {
        const Icon = cat.icon;
        return (
          <button
            key={cat.path + cat.title}
            onClick={() => handleCategoryClick(cat.path)}
            style={{
              ...cardBase,
              position: "relative",
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: large ? 10 : 8,
              padding: large ? "10px 14px" : "8px 10px",
              cursor: "pointer",
              textAlign: "left",
              transition: "all .2s",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = cat.color;
              el.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = "var(--border)";
              el.style.transform = "translateY(0)";
            }}>
            {cat.badge && (
              <span
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  background: cat.color,
                  color: "#fff",
                  fontSize: large ? 10 : 9,
                  fontWeight: 800,
                  padding: large ? "2px 6px" : "1px 5px",
                  borderRadius: 999,
                }}>
                {cat.badge}
              </span>
            )}
            <div
              style={{
                width: large ? 34 : 26,
                height: large ? 34 : 26,
                borderRadius: large ? 9 : 7,
                background: `${cat.color}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
              <Icon size={large ? 17 : 13} style={{ color: cat.color }} />
            </div>
            <div
              style={{
                minWidth: 0,
                textAlign: "left",
              }}>
              <p
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: large ? 17 : 11,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {large && cat.shortTitle ? cat.shortTitle : cat.title}
              </p>
              <p
                style={{
                  color: "var(--muted)",
                  fontSize: large ? 12 : 9,
                  marginTop: large ? 3 : 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {cat.subtitle}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );

  if (isMobile) {
    const mobileTabs = [
      { icon: "🏠", label: t("navHome"), path: "/dashboard" },
      { icon: "ai", label: "Ustoz AI", path: "/ai-ustoz" },
      { icon: "📖", label: "O'rganish", path: "/learn" },
    ];

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--bg)",
        }}>
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            background: "rgba(10,14,23,.9)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid var(--border)",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 30, height: 30 }}>
              <img
                src="/logo.png"
                alt="AvtoMind"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
              {t("appName")}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowLang((p) => !p)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--muted)",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                }}>
                {lang === "oz" ?
                  "O'Z"
                : lang === "uz" ?
                  "Ўз"
                : "RU"}
              </button>

              {showLang && (
                <div
                  className="fade-in"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "120%",
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    overflow: "hidden",
                    minWidth: 130,
                    zIndex: 60,
                    boxShadow: "0 8px 32px rgba(0,0,0,.4)",
                  }}>
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        setLang(l.code as any);
                        setShowLang(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "9px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        background: lang === l.code ? BRAND_BG : "transparent",
                        color: lang === l.code ? BRAND : "var(--muted)",
                        cursor: "pointer",
                        border: "none",
                        display: "block",
                      }}>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={toggleFullscreen}
              title="To'liq ekran"
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}>
              {isFullscreen ?
                <Minimize size={13} />
              : <Maximize size={13} />}
            </button>
            <button
              onClick={() => navigate("/notifications")}
              style={{
                position: "relative",
                width: 30,
                height: 30,
                borderRadius: "50%",
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}>
              <Bell size={13} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -3,
                    right: -3,
                    minWidth: 14,
                    height: 14,
                    padding: "0 3px",
                    borderRadius: 999,
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: 8,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate("/settings")}
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: BRAND,
                border: "none",
                color: "#fff",
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
              }}>
              {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
            </button>
          </div>
        </header>

        {showLang && (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 55 }}
            onClick={() => setShowLang(false)}
          />
        )}

        <div
          style={{
            padding: "10px 16px 130px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}>
          {isAdmin && (
            <button
              onClick={() => navigate("/admin")}
              style={{
                ...cardBase,
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                cursor: "pointer",
                textAlign: "left",
                background: BRAND_BG,
                borderColor: BRAND_BORDER,
              }}>
              <Shield size={15} style={{ color: BRAND }} />
              <span
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  flex: 1,
                }}>
                {t("adminPanel")}
              </span>
              <ChevronRight size={15} style={{ color: "var(--muted)" }} />
            </button>
          )}

          {/* PROGRESS + mini stats */}
          <div style={{ ...cardBase, padding: "12px 14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}>
              <span
                style={{
                  color: "var(--muted)",
                  fontWeight: 700,
                  fontSize: 12,
                }}>
                {t("readiness")}
              </span>
              <span style={{ fontWeight: 800, fontSize: 13, color: BRAND }}>
                {readiness}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: "var(--border)",
                borderRadius: 4,
                overflow: "hidden",
              }}>
              <div
                style={{
                  height: "100%",
                  width: `${readiness}%`,
                  background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
                  borderRadius: 4,
                  transition: "width 1.2s ease",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
              {statItems.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon size={13} style={{ color: s.color }} />
                    <span
                      style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>
                      {s.value}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--muted)",
                        fontWeight: 600,
                      }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* HERO — 2 ustunli, yonma-yon */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            }}>
            {heroCategories.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.path}
                  onClick={() => handleCategoryClick(cat.path)}
                  className={i === 0 ? "hero-glow-blue" : "hero-glow-purple"}
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,.06)",
                    borderRadius: 14,
                    background: cat.gradient,
                    padding: "10px 10px",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}>
                    <span
                      style={{
                        display: "inline-block",
                        background: "rgba(255,255,255,.16)",
                        color: "#fff",
                        fontSize: 8,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 999,
                      }}>
                      {cat.badge}
                    </span>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 8,
                        background: "rgba(255,255,255,.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}>
                      <Icon size={13} style={{ color: "#fff" }} />
                    </div>
                  </div>
                  <div>
                    <p
                      style={{
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 12.5,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                      {cat.title}
                    </p>
                    <p
                      style={{
                        color: "rgba(255,255,255,.75)",
                        fontSize: 9.5,
                        fontWeight: 600,
                        marginTop: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                      {cat.subtitle}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          <CategoryGrid
            items={testCategories}
            columns={2}
            delayClass="s2"
            large
          />

          <CategoryGrid
            items={gameCategories.slice(0, 2)}
            columns={2}
            delayClass="s4"
            large
          />

          {communityCategories.length > 0 && (
            <>
              <SectionDivider label={t("sectionCommunity")} color="#f59e0b" />
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {communityCategories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.path}
                      onClick={() => handleCategoryClick(cat.path)}
                      style={{
                        ...cardBase,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "13px 16px",
                        minHeight: 44,
                        cursor: "pointer",
                        textAlign: "left",
                      }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          minWidth: 0,
                        }}>
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: `${cat.color}22`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}>
                          <Icon size={16} style={{ color: cat.color }} />
                        </div>
                        <div>
                          <p
                            style={{
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: 13,
                            }}>
                            {cat.title}
                          </p>
                          <p
                            style={{
                              color: "var(--muted)",
                              fontSize: 10,
                              marginTop: 1,
                            }}>
                            {cat.subtitle}
                          </p>
                        </div>
                      </div>
                      <ChevronRight
                        size={16}
                        style={{ color: "var(--muted)" }}
                      />
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* BOTTOM NAV */}
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "10px 8px calc(10px + env(safe-area-inset-bottom))",
            background:
              "linear-gradient(180deg, rgba(10,14,23,0), rgba(10,14,23,.95) 30%)",
            zIndex: 99,
          }}>
          <nav
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 76px 1fr",
              alignItems: "end",
              background: "rgba(16,23,38,.95)",
              border: "1px solid var(--border)",
              borderRadius: 24,
              padding: "9px 6px",
              backdropFilter: "blur(14px)",
            }}>
            {mobileTabs.map((tab) => {
              const active = location.pathname === tab.path;
              if (tab.icon === "ai") {
                return (
                  <div
                    key={tab.path}
                    style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      onClick={() => navigate(tab.path)}
                      className="ai-fab-pulse"
                      style={{
                        width: 58,
                        height: 58,
                        marginTop: -30,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #2563eb, #3b82f6 60%, #6366f1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "4px solid var(--bg)",
                        cursor: "pointer",
                      }}>
                      <div style={{ textAlign: "center" }}>
                        <Sparkles
                          size={16}
                          className="ai-sparkle"
                          style={{ color: "#fff", margin: "0 auto" }}
                        />
                        <div
                          style={{
                            fontSize: 8.5,
                            fontWeight: 800,
                            color: "#fff",
                            marginTop: 1,
                          }}>
                          {tab.label}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              }
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    padding: 4,
                    minHeight: 44,
                    justifyContent: "center",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}>
                  <span style={{ fontSize: 18, opacity: active ? 1 : 0.55 }}>
                    {tab.icon}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: active ? 800 : 700,
                      color: active ? "#7db2ff" : "var(--muted)",
                    }}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
      }}>
      <nav
        style={{
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 110,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: "var(--content-max-width)",
            margin: "0 auto",
            padding: "0 20px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32 }}>
                <img
                  src="/logo.png"
                  alt="AvtoMind"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
                {t("appName")}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {navLinks.map((l) => (
                <button
                  key={l.path}
                  onClick={() => navigate(l.path)}
                  style={{
                    padding: "7px 14px",
                    borderRadius: 9,
                    border: "1px solid transparent",
                    background:
                      location.pathname === l.path ? BRAND_BG : "transparent",
                    color:
                      location.pathname === l.path ? BRAND : "var(--muted)",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isAdmin && (
              <button
                onClick={() => navigate("/admin")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 9,
                  border: `1px solid ${BRAND_BORDER}`,
                  background: BRAND_BG,
                  color: BRAND,
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: "pointer",
                }}>
                <Shield size={12} /> Admin
              </button>
            )}

            <div style={{ position: "relative" }}>
              <button
                onClick={() => {
                  setShowLang((p) => !p);
                  setShowUser(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "6px 9px",
                  borderRadius: 9,
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--muted)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}>
                <Globe size={12} />
                {lang === "oz" ?
                  "O'Z"
                : lang === "uz" ?
                  "Ўз"
                : "RU"}
              </button>

              {showLang && (
                <div
                  className="fade-in"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "110%",
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    overflow: "hidden",
                    minWidth: 130,
                    boxShadow: "0 8px 32px rgba(0,0,0,.4)",
                  }}>
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        setLang(l.code as any);
                        setShowLang(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "9px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        background: lang === l.code ? BRAND_BG : "transparent",
                        color: lang === l.code ? BRAND : "var(--muted)",
                        cursor: "pointer",
                        border: "none",
                        display: "block",
                      }}>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/contacts")}
              title="Aloqa va kanallar"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                cursor: "pointer",
              }}>
              <MessageSquare size={12} />
            </button>

            <button
              onClick={toggleFullscreen}
              title="To'liq ekran"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                cursor: "pointer",
              }}>
              {isFullscreen ?
                <Minimize size={12} />
              : <Maximize size={12} />}
            </button>

            <button
              onClick={() => navigate("/notifications")}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                cursor: "pointer",
              }}>
              <Bell size={12} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    minWidth: 15,
                    height: 15,
                    padding: "0 3px",
                    borderRadius: 999,
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                  }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            <div style={{ position: "relative" }}>
              <button
                onClick={() => {
                  setShowUser((p) => !p);
                  setShowLang(false);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: BRAND,
                  border: "none",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
              </button>

              {showUser && (
                <div
                  className="fade-in"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "110%",
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    overflow: "hidden",
                    minWidth: 180,
                    boxShadow: "0 8px 32px rgba(0,0,0,.4)",
                  }}>
                  <div
                    style={{
                      padding: "10px 14px",
                      borderBottom: "1px solid var(--border)",
                    }}>
                    <p style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                      {user?.fullName}
                    </p>
                    <p
                      style={{
                        color: "var(--muted)",
                        fontSize: 11,
                        marginTop: 2,
                        wordBreak: "break-word",
                      }}>
                      {user?.email}
                    </p>
                  </div>

                  {[
                    { icon: User, label: t("profile"), path: "/settings" },
                    {
                      icon: BarChart2,
                      label: t("statistics"),
                      path: "/statistics",
                    },
                    {
                      icon: MessageSquare,
                      label: t("catContacts"),
                      path: "/contacts",
                    },
                    { icon: Settings, label: t("settings"), path: "/settings" },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => {
                          navigate(item.path);
                          setShowUser(false);
                        }}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "9px 14px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--muted)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "left",
                        }}>
                        <Icon size={13} /> {item.label}
                      </button>
                    );
                  })}

                  <div style={{ borderTop: "1px solid var(--border)" }}>
                    <button
                      onClick={() => {
                        logout();
                        navigate("/auth");
                      }}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "9px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#EF4444",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}>
                      <LogOut size={13} /> {t("logout")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {(showLang || showUser) && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 99 }}
          onClick={() => {
            setShowLang(false);
            setShowUser(false);
          }}
        />
      )}

      <div
        style={{
          flex: 1,
          maxWidth: "var(--content-max-width)",
          width: "100%",
          margin: "0 auto",
          padding: "10px 20px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
        {/* STATS — tayyorlik + 3ta ko'rsatkich */}
        <div
          className="fade-up s1"
          style={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
            gap: 8,
            flexShrink: 0,
          }}>
          <div style={{ ...cardBase, padding: "9px 12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 5,
              }}>
              <span
                style={{
                  color: "var(--muted)",
                  fontWeight: 600,
                  fontSize: 11,
                }}>
                {t("readiness")}
              </span>
              <span style={{ fontWeight: 800, fontSize: 12, color: BRAND }}>
                {readiness}%
              </span>
            </div>

            <div
              style={{
                height: 5,
                background: "var(--border)",
                borderRadius: 99,
                overflow: "hidden",
              }}>
              <div
                style={{
                  height: "100%",
                  width: `${readiness}%`,
                  background: BRAND,
                  borderRadius: 99,
                  transition: "width 1.2s ease",
                }}
              />
            </div>

            <p style={{ color: "var(--muted)", fontSize: 10, marginTop: 4 }}>
              {readiness >= 80 ?
                t("readyForExam")
              : readiness >= 50 ?
                t("keepGoing")
              : t("practiceMore")}
            </p>
          </div>

          {statItems.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                style={{
                  ...cardBase,
                  padding: "10px 10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 6,
                }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: `${s.color}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  <Icon size={14} style={{ color: s.color }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      color: "#fff",
                      fontSize: 20,
                      fontWeight: 800,
                      lineHeight: 1.1,
                    }}>
                    {s.value}
                  </p>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {s.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* HERO — Real imtihon + Oktagon */}
        <div
          className="fade-up s2"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            flexShrink: 0,
          }}>
          {heroCategories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.path}
                onClick={() => handleCategoryClick(cat.path)}
                className={i === 0 ? "hero-glow-blue" : "hero-glow-purple"}
                style={{
                  position: "relative",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,.06)",
                  borderRadius: 16,
                  background: cat.gradient,
                  padding: "10px 16px",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 72,
                  transition: "transform .2s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.transform =
                    "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.transform =
                    "translateY(0)";
                }}>
                <span
                  style={{
                    alignSelf: "flex-start",
                    background: "rgba(255,255,255,.12)",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 999,
                  }}>
                  {cat.badge}
                </span>

                <div>
                  <p
                    style={{
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 14,
                      marginBottom: 1,
                    }}>
                    {cat.title}
                  </p>
                  <p
                    style={{
                      color: "rgba(255,255,255,.75)",
                      fontSize: 10,
                      fontWeight: 600,
                    }}>
                    {cat.subtitle}
                  </p>
                </div>

                <div
                  style={{
                    position: "absolute",
                    right: 12,
                    bottom: 10,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <Icon size={14} style={{ color: "#fff" }} />
                </div>
              </button>
            );
          })}
        </div>

        <SectionDivider label="Testlar" color={BRAND} />
        <CategoryGrid
          items={testCategories}
          columns={4}
          large
          delayClass="s2"
        />

        {/* IZOHLI TESTLAR — banner */}
        <button
          onClick={comingSoon}
          className="fade-up s3"
          style={{
            width: "100%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 14px",
            borderRadius: 14,
            border: "1px solid rgba(139,92,246,.3)",
            background: "linear-gradient(90deg, #4c1d95, #1e3a8a)",
            cursor: "pointer",
            textAlign: "left",
          }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: "rgba(255,255,255,.14)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
            <Headphones size={15} style={{ color: "#fff" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <p style={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
                {t("explainedTests")}
              </p>
              <span
                style={{
                  background: "#f59e0b",
                  color: "#1a1305",
                  fontSize: 8,
                  fontWeight: 800,
                  padding: "1px 5px",
                  borderRadius: 999,
                }}>
                YANGI
              </span>
            </div>
            <p
              style={{
                color: "rgba(255,255,255,.7)",
                fontSize: 10,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
              {t("explainedTestsSub")}
            </p>
          </div>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: BRAND,
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              padding: "6px 12px",
              borderRadius: 9,
              flexShrink: 0,
            }}>
            <Play size={11} /> {t("tryIt")}
          </span>
        </button>

        <SectionDivider label={t("catGames")} color="#a78bfa" />
        <CategoryGrid
          items={gameCategories}
          columns={4}
          large
          delayClass="s3"
        />

        <SectionDivider label="O'rganish" color="#22c55e" />
        <CategoryGrid
          items={learnCategories}
          columns={4}
          large
          delayClass="s4"
        />

        {communityCategories.length > 0 && (
          <>
            <SectionDivider label="Hamjamiyat" color="#f59e0b" />
            <CategoryGrid
              items={communityCategories}
              columns={communityCategories.length}
              delayClass="s5"
            />
          </>
        )}

        {/* USTOZ AI ostida joy qoldirish uchun spacer */}
        <div style={{ height: 56, flexShrink: 0 }} />
      </div>

      {/* USTOZ AI — doim ko'rinadigan suzuvchi tugma */}
      <button
        onClick={() => navigate("/ai-ustoz")}
        className="ustoz-glow"
        style={{
          position: "fixed",
          left: "50%",
          bottom: 18,
          transform: "translateX(-50%)",
          zIndex: 90,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 20px",
          borderRadius: 999,
          border: `1px solid ${BRAND_BORDER}`,
          background: "rgba(15,23,42,.95)",
          backdropFilter: "blur(10px)",
          cursor: "pointer",
        }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <Sparkles size={13} style={{ color: "#fff" }} />
        </div>
        <div style={{ textAlign: "left" }}>
          <p style={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
            Ustoz AI
          </p>
          <p
            style={{
              color: "var(--muted)",
              fontSize: 9,
              fontWeight: 600,
            }}>
            {t("catAISub")}
          </p>
        </div>
        <div
          className="shimmer-bar"
          style={{
            position: "absolute",
            left: 10,
            right: 10,
            bottom: 3,
            height: 2,
            borderRadius: 999,
          }}
        />
      </button>
    </div>
  );
};

export default DashboardPage;
